// src/pages/Dashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase.js";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

const COLORS = ["#2563EB", "#F97316", "#22C55E", "#A855F7"];

function Dashboard() {
  const [soumissions, setSoumissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedTab, setSelectedTab] = useState("roles"); // roles | dates | villes
  const [selectedRole, setSelectedRole] = useState("Tous");
  const [selectedVille, setSelectedVille] = useState("Toutes");
  const [periodFilter, setPeriodFilter] = useState("toutes"); // toutes | today | 7 | 30
  const [isDarkMode, setIsDarkMode] = useState(false);

  const auth = getAuth();
  const navigate = useNavigate();
  const adminEmail = "modyhady94@gmail.com"; // <-- ton mail admin

  // 🔐 Vérification admin + récupération des données
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate("/connexion");
      return;
    }
    if (user.email !== adminEmail) {
      alert("⛔ Accès refusé. Dashboard réservé à l’administrateur.");
      navigate("/");
      return;
    }

    const fetchSoumissions = async () => {
      try {
        const snapshot = await getDocs(collection(db, "soumissions"));
        const data = snapshot.docs.map((doc) => {
          const d = doc.data();
          let date = null;
          if (d.createdAT?.toDate) {
            date = d.createdAT.toDate();
          }
          return { id: doc.id, ...d, createdAT: date };
        });
        setSoumissions(data);
      } catch (error) {
        console.error("Erreur Dashboard Firestore :", error);
        alert("Impossible de charger les données du dashboard ❌");
      } finally {
        setLoading(false);
      }
    };

    fetchSoumissions();
  }, [auth, navigate]);

  // 📌 Liste des villes existantes
  const villesUniques = useMemo(
    () =>
      Array.from(new Set(soumissions.map((s) => s.ville).filter(Boolean))).sort(),
    [soumissions]
  );

  const villeEffective = selectedVille === "Toutes" ? null : selectedVille || null;

  // 🔍 Filtres combinés (rôle + ville + période)
  const filteredSoumissions = soumissions.filter((s) => {
    const okRole = selectedRole === "Tous" || s.role === selectedRole;
    const okVille = !villeEffective || s.ville === villeEffective;

    if (periodFilter === "toutes") {
      return okRole && okVille;
    }

    if (!s.createdAT) return false;

    const now = new Date();
    const diffMs = now - s.createdAT;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    let okDate = true;

    if (periodFilter === "today") {
      const d1 = now.toISOString().slice(0, 10);
      const d2 = s.createdAT.toISOString().slice(0, 10);
      okDate = d1 === d2;
    } else if (periodFilter === "7") {
      okDate = diffDays <= 7;
    } else if (periodFilter === "30") {
      okDate = diffDays <= 30;
    }

    return okRole && okVille && okDate;
  });

  // 🧮 Agrégations (SUR LES DONNÉES FILTRÉES)
  const total = filteredSoumissions.length;
  const totalEleves = filteredSoumissions.filter((s) => s.role === "eleve").length;
  const totalTuteurs = filteredSoumissions.filter((s) => s.role === "tuteur").length;
  const totalEcoles = filteredSoumissions.filter((s) => s.role === "ecole").length;

  // 📊 Répartition par rôle
  const roleStats = [
    { name: "Élèves", value: totalEleves },
    { name: "Tuteurs", value: totalTuteurs },
    { name: "Écoles", value: totalEcoles },
  ];

  // 📅 Évolution par date (filtré)
  const parDateMap = {};
  filteredSoumissions.forEach((s) => {
    if (!s.createdAT) return;
    const key = s.createdAT.toISOString().slice(0, 10);
    if (!parDateMap[key]) parDateMap[key] = 0;
    parDateMap[key] += 1;
  });
  const evolutionParDate = Object.keys(parDateMap)
    .sort()
    .map((d) => ({ date: d, value: parDateMap[d] }));

  // 📍 Stats par ville (filtré)
  const parVilleMap = {};
  filteredSoumissions.forEach((s) => {
    const v = s.ville || "Non renseignée";
    if (!parVilleMap[v]) parVilleMap[v] = 0;
    parVilleMap[v] += 1;
  });
  const statsParVille = Object.keys(parVilleMap).map((v) => ({
    ville: v,
    value: parVilleMap[v],
  }));

  // ✅ Top 5 villes (UI uniquement)
  const topVilles = useMemo(() => {
    return [...statsParVille].sort((a, b) => b.value - a.value).slice(0, 5);
  }, [statsParVille]);

  // ⭐ Stats avancées
  let jourMax = null;
  let maxSoumJour = 0;
  evolutionParDate.forEach((d) => {
    if (d.value > maxSoumJour) {
      maxSoumJour = d.value;
      jourMax = d.date;
    }
  });

  const roleDominant = roleStats.reduce(
    (max, r) => (r.value > max.value ? r : max),
    { name: "—", value: 0 }
  );

  const villeTop = statsParVille.reduce(
    (max, v) => (v.value > max.value ? v : max),
    { ville: "—", value: 0 }
  );

  // 📤 Export CSV (filtré)
  const handleExportCSV = () => {
    if (filteredSoumissions.length === 0) {
      alert("Aucune donnée à exporter pour ce filtre.");
      return;
    }

    const headers = [
      "Nom",
      "Email",
      "Téléphone",
      "Rôle",
      "Ville",
      "École",
      "Matière",
      "Message",
      "Date",
    ];

    const rows = filteredSoumissions.map((s) => [
      s.nom || "",
      s.email || "",
      s.telephone || "",
      s.role || "",
      s.ville || "",
      s.ecole || "",
      s.matiere || "",
      (s.message || "").replace(/\r?\n/g, " "),
      s.createdAT ? s.createdAT.toLocaleString() : "",
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "soumissions_hadycom_dashboard.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ✅ Reset filtres (UI uniquement)
  const handleResetFilters = () => {
    setSelectedRole("Tous");
    setSelectedVille("Toutes");
    setPeriodFilter("toutes");
    setSelectedTab("roles");
  };

  // 🎨 Thème
  const theme = isDarkMode
    ? {
        bg: "#0B1220",
        panel: "rgba(17,24,39,0.78)",
        card: "rgba(2,6,23,0.65)",
        border: "rgba(148,163,184,0.14)",
        text: "#E5E7EB",
        sub: "rgba(229,231,235,0.75)",
      }
    : {
        bg: "radial-gradient(1200px 700px at 15% 0%, rgba(37,99,235,0.12) 0%, rgba(255,255,255,0) 55%), linear-gradient(180deg, #ffffff 0%, #F3F4FF 100%)",
        panel: "rgba(255,255,255,0.75)",
        card: "rgba(255,255,255,0.95)",
        border: "rgba(37,99,235,0.12)",
        text: "#0F172A",
        sub: "#475569",
      };

  const pageStyle = { ...styles.page, background: theme.bg, color: theme.text };
  const panelStyle = { ...styles.panel, background: theme.panel, border: `1px solid ${theme.border}` };
  const cardStyle = { ...styles.card, background: theme.card, border: `1px solid ${theme.border}` };
  const selectStyle = {
    ...styles.select,
    background: isDarkMode ? "rgba(2,6,23,0.7)" : "#fff",
    color: theme.text,
    border: `1px solid ${theme.border}`,
  };
  const tabsWrapStyle = {
    ...styles.tabsWrap,
    background: isDarkMode ? "rgba(2,6,23,0.55)" : "rgba(229,235,255,0.9)",
    border: `1px solid ${theme.border}`,
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ ...styles.loadingWrap, ...panelStyle }}>
          <div style={styles.spinner} />
          <p style={{ margin: 0, color: theme.sub, fontWeight: 800 }}>Chargement du dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{ ...styles.topbar, ...panelStyle }}>
        <div style={styles.topbarLeft}>
          <div style={styles.badgeIcon}>📊</div>
          <div>
            <h1 style={{ ...styles.title, color: theme.text }}>Dashboard Administrateur</h1>
            <p style={{ ...styles.subtitle, color: theme.sub }}>
              Vue globale, filtres avancés et visualisations
            </p>
          </div>
        </div>

        <div style={styles.topbarRight}>
          <button
            onClick={() => navigate("/admin")}
            style={{
              ...styles.secondaryBtn,
              background: isDarkMode ? "rgba(148,163,184,0.10)" : "rgba(37,99,235,0.08)",
              color: theme.text,
              border: `1px solid ${theme.border}`,
            }}
          >
            ⬅️ Retour Admin
          </button>

          <button
            onClick={handleResetFilters}
            style={{
              ...styles.secondaryBtn,
              background: isDarkMode ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)",
              color: theme.text,
              border: `1px solid ${theme.border}`,
            }}
            title="Réinitialiser tous les filtres"
          >
            🔄 Réinitialiser
          </button>

          <button
            onClick={() => setIsDarkMode((prev) => !prev)}
            style={{
              ...styles.darkModeBtn,
              background: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(2,6,23,0.90)",
              color: isDarkMode ? theme.text : "#F9FAFB",
              border: isDarkMode ? `1px solid ${theme.border}` : "1px solid rgba(255,255,255,0.18)",
            }}
          >
            {isDarkMode ? "☀️ Mode clair" : "🌙 Mode sombre"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.cardsRow}>
        {[
          { label: "Total (filtré)", value: total, icon: "🧾", accent: "#2563EB" },
          { label: "Élèves", value: totalEleves, icon: "👨‍🎓", accent: "#A855F7" },
          { label: "Tuteurs", value: totalTuteurs, icon: "👨‍🏫", accent: "#F97316" },
          { label: "Écoles", value: totalEcoles, icon: "🏫", accent: "#22C55E" },
        ].map((c, i) => (
          <div key={i} style={{ ...cardStyle, ...styles.statCard }}>
            <div style={{ ...styles.statIcon, background: `${c.accent}20`, border: `1px solid ${c.accent}30` }}>
              {c.icon}
            </div>
            <div style={styles.statBody}>
              <p style={{ ...styles.cardLabel, color: theme.sub }}>{c.label}</p>
              <p style={{ ...styles.cardValue, color: theme.text }}>{c.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div style={{ ...styles.filtersBar, ...panelStyle }}>
        <div style={styles.filtersLeft}>
          <div style={styles.field}>
            <span style={{ ...styles.fieldLabel, color: theme.sub }}>Rôle</span>
            <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} style={selectStyle}>
              <option value="Tous">Tous</option>
              <option value="eleve">Élèves</option>
              <option value="tuteur">Tuteurs</option>
              <option value="ecole">Écoles</option>
            </select>
          </div>

          <div style={styles.field}>
            <span style={{ ...styles.fieldLabel, color: theme.sub }}>Ville</span>
            <select value={selectedVille} onChange={(e) => setSelectedVille(e.target.value)} style={selectStyle}>
              <option value="Toutes">Toutes</option>
              {villesUniques.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.field}>
            <span style={{ ...styles.fieldLabel, color: theme.sub }}>Période</span>
            <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)} style={selectStyle}>
              <option value="toutes">Toutes</option>
              <option value="today">Aujourd’hui</option>
              <option value="7">7 derniers jours</option>
              <option value="30">30 derniers jours</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleExportCSV}
          style={{
            ...styles.primaryBtn,
            background: "linear-gradient(135deg, #16A34A 0%, #22C55E 100%)",
          }}
        >
          ⬇️ Exporter (CSV)
        </button>
      </div>

      {/* Tabs */}
      <div style={tabsWrapStyle}>
        <button
          style={{
            ...styles.tab,
            ...(selectedTab === "roles" ? styles.tabActive : {}),
            color: selectedTab === "roles" ? "#fff" : theme.text,
          }}
          onClick={() => setSelectedTab("roles")}
        >
          Répartition des rôles
        </button>
        <button
          style={{
            ...styles.tab,
            ...(selectedTab === "dates" ? styles.tabActive : {}),
            color: selectedTab === "dates" ? "#fff" : theme.text,
          }}
          onClick={() => setSelectedTab("dates")}
        >
          Évolution par date
        </button>
        <button
          style={{
            ...styles.tab,
            ...(selectedTab === "villes" ? styles.tabActive : {}),
            color: selectedTab === "villes" ? "#fff" : theme.text,
          }}
          onClick={() => setSelectedTab("villes")}
        >
          Soumissions par ville
        </button>
      </div>

      {/* Charts */}
      <div style={styles.chartsGrid}>
        <div style={{ ...cardStyle, ...styles.chartContainer }}>
          {selectedTab === "roles" && (
            <>
              <div style={styles.chartHead}>
                <h3 style={{ ...styles.chartTitle, color: theme.text }}>Répartition des rôles</h3>
                <span style={{ ...styles.chartHint, color: theme.sub }}>Données filtrées</span>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={roleStats}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label
                  >
                    {roleStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </>
          )}

          {selectedTab === "dates" && (
            <>
              <div style={styles.chartHead}>
                <h3 style={{ ...styles.chartTitle, color: theme.text }}>Évolution des soumissions</h3>
                <span style={{ ...styles.chartHint, color: theme.sub }}>Par jour</span>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={evolutionParDate}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#2563EB" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}

          {selectedTab === "villes" && (
            <>
              <div style={styles.chartHead}>
                <h3 style={{ ...styles.chartTitle, color: theme.text }}>Soumissions par ville</h3>
                <span style={{ ...styles.chartHint, color: theme.sub }}>Top localisations</span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={statsParVille}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="ville" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#22C55E" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>

              {/* ✅ TOP 5 villes (UI uniquement) */}
              <div style={{ ...styles.topList, border: `1px solid ${theme.border}`, background: isDarkMode ? "rgba(148,163,184,0.06)" : "rgba(37,99,235,0.04)" }}>
                <div style={styles.topListHead}>
                  <span style={{ ...styles.topListTitle, color: theme.text }}>🏆 Top 5 villes</span>
                  <span style={{ ...styles.topListHint, color: theme.sub }}>sur les données filtrées</span>
                </div>
                {topVilles.length === 0 ? (
                  <p style={{ margin: 0, color: theme.sub, fontWeight: 800 }}>Aucune donnée.</p>
                ) : (
                  <div style={styles.topListRows}>
                    {topVilles.map((v, idx) => (
                      <div key={v.ville} style={styles.topRow}>
                        <div style={styles.topLeft}>
                          <span style={{ ...styles.rank, border: `1px solid ${theme.border}`, color: theme.text }}>
                            {idx + 1}
                          </span>
                          <span style={{ ...styles.cityName, color: theme.text }}>{v.ville}</span>
                        </div>
                        <span style={{ ...styles.countPill, border: `1px solid ${theme.border}`, color: theme.text }}>
                          {v.value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div style={{ ...cardStyle, ...styles.chartContainer }}>
          <div style={styles.chartHead}>
            <h3 style={{ ...styles.chartTitle, color: theme.text }}>Localisation & statistiques avancées</h3>
            <span style={{ ...styles.chartHint, color: theme.sub }}>Résumé intelligent</span>
          </div>

          <div style={styles.advancedStats}>
            {[
              { label: "Jour le plus actif", value: jourMax ? `${jourMax} (${maxSoumJour})` : "—", icon: "📆" },
              { label: "Rôle dominant", value: `${roleDominant.name} (${roleDominant.value})`, icon: "👤" },
              { label: "Ville la plus demandée", value: `${villeTop.ville} (${villeTop.value})`, icon: "📍" },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  ...styles.kpi,
                  border: `1px solid ${theme.border}`,
                  background: isDarkMode ? "rgba(148,163,184,0.06)" : "rgba(37,99,235,0.04)",
                }}
              >
                <div style={styles.kpiIcon}>{s.icon}</div>
                <div>
                  <p style={{ ...styles.kpiLabel, color: theme.sub }}>{s.label}</p>
                  <p style={{ ...styles.kpiValue, color: theme.text }}>{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 14 }}>
            <p style={{ ...styles.mapLabel, color: theme.sub }}>
              Carte — {villeEffective || "Toutes les villes (vue générale)"}
            </p>
            <div style={{ ...styles.mapWrapper, border: `1px solid ${theme.border}` }}>
              <iframe
                title="Carte des soumissions"
                width="100%"
                height="240"
                style={{ border: 0, borderRadius: 16 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${
                  villeEffective ? encodeURIComponent(villeEffective) : encodeURIComponent("France")
                }&output=embed`}
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* 🎨 Styles (UI uniquement) */
const styles = {
  page: {
    minHeight: "100vh",
    padding: "26px 5%",
    transition: "background 0.3s ease, color 0.3s ease",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  loadingWrap: {
    maxWidth: 520,
    margin: "80px auto 0",
    padding: 22,
    borderRadius: 18,
    display: "flex",
    gap: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    border: "3px solid rgba(37,99,235,0.18)",
    borderTop: "3px solid rgba(37,99,235,0.95)",
    animation: "spin 1s linear infinite",
  },

  panel: {
    borderRadius: 18,
    padding: 16,
    boxShadow: "0 18px 60px rgba(15,23,42,0.08)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  },

  topbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    marginBottom: 18,
  },
  topbarLeft: { display: "flex", alignItems: "center", gap: 12 },
  badgeIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(135deg, rgba(37,99,235,0.20) 0%, rgba(168,85,247,0.18) 100%)",
    border: "1px solid rgba(37,99,235,0.18)",
    fontSize: 22,
  },
  title: { margin: 0, fontSize: 22, fontWeight: 900, letterSpacing: "-0.02em" },
  subtitle: { margin: "4px 0 0 0", fontSize: 13, fontWeight: 700 },

  topbarRight: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  darkModeBtn: {
    padding: "10px 14px",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
    boxShadow: "0 10px 28px rgba(2,6,23,0.22)",
    transition: "transform .2s ease, box-shadow .2s ease",
  },
  secondaryBtn: {
    padding: "10px 14px",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
    transition: "transform .2s ease, box-shadow .2s ease",
  },
  primaryBtn: {
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
    boxShadow: "0 12px 30px rgba(34,197,94,0.28)",
    transition: "transform .2s ease, box-shadow .2s ease",
    whiteSpace: "nowrap",
  },

  cardsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))",
    gap: 14,
    marginBottom: 14,
  },
  card: {
    borderRadius: 18,
    padding: "16px 16px",
    boxShadow: "0 16px 50px rgba(15,23,42,0.08)",
    transition: "transform .2s ease, box-shadow .2s ease",
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  statIcon: {
    width: 46,
    height: 46,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
  },
  statBody: { display: "flex", flexDirection: "column", gap: 2 },
  cardLabel: { margin: 0, fontSize: 12, fontWeight: 900 },
  cardValue: { margin: 0, fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em" },

  filtersBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  filtersLeft: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  fieldLabel: { fontSize: 12, fontWeight: 900 },
  select: {
    padding: "10px 12px",
    borderRadius: 14,
    fontSize: 13,
    fontWeight: 900,
    outline: "none",
    minWidth: 160,
  },

  tabsWrap: {
    display: "inline-flex",
    padding: 6,
    borderRadius: 999,
    gap: 6,
    marginBottom: 14,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  },
  tab: {
    borderRadius: 999,
    border: "none",
    padding: "9px 14px",
    fontSize: 13,
    cursor: "pointer",
    background: "transparent",
    fontWeight: 900,
    transition: "all .2s ease",
    whiteSpace: "nowrap",
  },
  tabActive: {
    background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 50%, #A855F7 120%)",
    boxShadow: "0 14px 36px rgba(37,99,235,0.28)",
  },

  chartsGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 2fr)",
    gap: 16,
  },
  chartContainer: {
    padding: 18,
  },
  chartHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 10,
    marginBottom: 10,
  },
  chartTitle: { margin: 0, fontSize: 15, fontWeight: 900 },
  chartHint: { fontSize: 12, fontWeight: 900 },

  advancedStats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
    gap: 10,
  },
  kpi: {
    borderRadius: 16,
    padding: 12,
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  kpiIcon: {
    width: 36,
    height: 36,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(37,99,235,0.12)",
    border: "1px solid rgba(37,99,235,0.18)",
  },
  kpiLabel: { margin: 0, fontSize: 12, fontWeight: 900 },
  kpiValue: { margin: "2px 0 0 0", fontSize: 13, fontWeight: 900 },

  // ✅ Top list
  topList: {
    marginTop: 14,
    borderRadius: 16,
    padding: 12,
  },
  topListHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 10,
    marginBottom: 10,
  },
  topListTitle: { fontSize: 13, fontWeight: 900 },
  topListHint: { fontSize: 12, fontWeight: 900 },
  topListRows: { display: "flex", flexDirection: "column", gap: 8 },
  topRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 10px",
    borderRadius: 14,
    background: "rgba(255,255,255,0.35)",
  },
  topLeft: { display: "flex", alignItems: "center", gap: 10 },
  rank: {
    width: 28,
    height: 28,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 900,
    background: "rgba(37,99,235,0.10)",
  },
  cityName: { fontSize: 13, fontWeight: 900 },
  countPill: {
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    background: "rgba(37,99,235,0.10)",
  },

  mapLabel: { margin: "10px 0 8px 0", fontSize: 12, fontWeight: 900 },
  mapWrapper: {
    borderRadius: 18,
    overflow: "hidden",
  },
};

// Keyframes
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  button:hover {
    transform: translateY(-1px);
  }
`;
document.head.appendChild(styleSheet);

export default Dashboard;
